var __makeTemplateObject = this && this.__makeTemplateObject || function (cooked, raw) {
  if (Object.defineProperty) {
    Object.defineProperty(cooked, "raw", {
      value: raw
    });
  } else {
    cooked.raw = raw;
  }
  return cooked;
};
var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import React from 'react';
import styled, { css } from 'styled-components';
import { breakpoints } from '@shapeable/theme';
import { classNames, clockAngle, cPointAtOrigin, propMerge } from '@shapeable/utils';
import { SvgCurvedLine } from '../elements/svg-curved-line';
import { SvgCompassStar } from '../elements/svg-compass-star';
import { SvgCircle } from '../elements/svg-circle';
import { SvgDonutHoleMask } from '../elements/svg-donut-hole-mask';
import { SvgEntityNode } from './svg-entity-node';
import { SvgCanvas } from '../elements/svg-canvas';
import { useStaticColorsFrom } from '../hooks/use-theme-colors';
import { useEntity } from '../hooks/use-entity';
import { useView } from '../hooks/use-view';
var cls = classNames('svg-entity-navigator');
export var SvgEntityNavigatorDefaultProps = {
  nodes: [],
  holeRadius: 10,
  showStar: true,
  showMajorEdges: true,
  showMinorEdges: true,
  showMinorNodes: true,
  showMajorNodes: true,
  animate: true,
  minorMode: 'active',
  edge: {
    colors: {
      stroke: 'bright'
    }
  },
  star: {
    colors: {
      fill: 'bright'
    }
  },
  outerRing: {
    colors: {
      stroke: 'bright'
    },
    majorNode: {
      r: 50,
      outlineNumber: {
        fontSize: 48
      },
      label: {
        fontSize: 18,
        gap: 10
      }
    },
    minorNode: {
      r: 20,
      outlineNumber: {
        fontSize: 20
      },
      label: {
        fontSize: 16,
        gap: 8
      }
    }
  },
  innerRing: {
    colors: {
      stroke: 'bright'
    },
    r: 150,
    childArc: 60,
    majorNode: {
      r: 50,
      outlineNumber: {
        fontSize: 13
      },
      label: {
        fontSize: 13,
        gap: 8
      }
    },
    minorNode: {
      r: 20,
      label: {
        fontSize: 15,
        gap: 8
      }
    }
  },
  paddingTop: 10,
  paddingBottom: 100,
  paddingLeft: 60,
  paddingRight: 60,
  nodeDelay: 80,
  lineDelay: 80
};
// -------- Styles -------->
var ContainerStyles = breakpoints({
  base: css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n    .shp--svg-canvas__canvas {\n      width: 100%;\n      height: 100%;\n    }\n\n    .shp--svg-canvas__controls {\n      right: 20px;\n      top: 0;\n    }\n  "], ["\n    .shp--svg-canvas__canvas {\n      width: 100%;\n      height: 100%;\n    }\n\n    .shp--svg-canvas__controls {\n      right: 20px;\n      top: 0;\n    }\n  "])))
});
// -------- Components -------->
var My = {
  NAME: 'SvgEntityNavigator',
  Container: styled(SvgCanvas)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["", ""], ["", ""])), ContainerStyles),
  OuterRing: SvgCircle,
  MinorRing: SvgCircle,
  MajorNode: SvgEntityNode,
  MinorNode: SvgEntityNode,
  MajorEdges: styled.g(templateObject_3 || (templateObject_3 = __makeTemplateObject([""], [""]))),
  MajorEdge: SvgCurvedLine,
  MinorEdges: styled.g(templateObject_4 || (templateObject_4 = __makeTemplateObject([""], [""]))),
  MinorEdge: SvgCurvedLine,
  Star: SvgCompassStar
};
export var SvgEntityNavigator = React.memo(function (props) {
  var className = props.className,
    nodes = props.nodes,
    holeRadius = props.holeRadius,
    paddingBottom = props.paddingBottom,
    paddingLeft = props.paddingLeft,
    paddingRight = props.paddingRight,
    paddingTop = props.paddingTop,
    nodeDelay = props.nodeDelay,
    lineDelay = props.lineDelay,
    outerRing = props.outerRing,
    innerRing = props.innerRing,
    edge = props.edge,
    star = props.star,
    showStar = props.showStar,
    showMajorEdges = props.showMajorEdges,
    showMinorEdges = props.showMinorEdges,
    showMinorNodes = props.showMinorNodes,
    showMajorNodes = props.showMajorNodes,
    minorMode = props.minorMode;
  console.log("RERENDER");
  // we may need to tweak this algorithm later to be more related to the numbers of nodes in each layer
  if (minorMode === 'active') {
    outerRing.r || (outerRing.r = innerRing.r * 2);
  }
  if (minorMode === 'all') {
    outerRing.r || (outerRing.r = innerRing.r * 3);
  }
  // calculate the width and height, based on the outer ring radius and outerNodeRadius
  var overallRadius = outerRing.r + outerRing.majorNode.r;
  var entity = useEntity();
  var view = useView(entity.path);
  var isOverview = (view === null || view === void 0 ? void 0 : view.activeView) === 'overview' || !(view === null || view === void 0 ? void 0 : view.activeView);
  var width = overallRadius * 2 + paddingLeft + paddingRight;
  var height = overallRadius * 2 + paddingTop + paddingBottom;
  var origin = {
    x: width / 2,
    y: height / 2
  };
  var staticColors = useStaticColorsFrom({
    outerRing: outerRing,
    innerRing: innerRing,
    edge: edge,
    star: star
  });
  var hasActiveMajorNode = nodes.some(function (node) {
    return node.isActive;
  });
  var hasActiveMinorNode = nodes.some(function (node) {
    return node.edges.some(function (edge) {
      return edge.node.isActive;
    });
  });
  var cPointReset = cPointAtOrigin(0, 0);
  var cPoint = cPointAtOrigin(origin.x, origin.y);
  var linesDelay = nodeDelay * nodes.length;
  var maskId = 'donut-hole';
  var isIndex = !hasActiveMajorNode;
  return React.createElement(My.Container, {
    downloadScaleFactor: minorMode === 'active' ? 4 : 8,
    initialX: 30,
    initialY: (paddingBottom - paddingTop) / 2,
    width: width,
    height: height,
    className: cls.name(className)
  }, React.createElement(SvgDonutHoleMask, {
    animate: isIndex,
    from: {
      r: 0
    },
    r: holeRadius,
    cx: origin.x,
    cy: origin.y,
    width: width,
    height: height,
    id: maskId
  }), React.createElement(My.OuterRing, {
    animate: isIndex,
    colors: staticColors.outerRing,
    strokeWidth: 2,
    cx: origin.x,
    cy: origin.y,
    r: outerRing.r,
    from: {
      r: 0
    }
  }), React.createElement(My.MinorRing, {
    animate: isIndex,
    colors: staticColors.innerRing,
    strokeWidth: 2,
    cx: origin.x,
    cy: origin.y,
    r: innerRing.r,
    from: {
      r: 0
    }
  }), showStar && React.createElement(My.Star, {
    animate: isIndex,
    mask: "url(#donut-hole)",
    springConfig: {
      tension: 100,
      friction: 10
    },
    cx: origin.x,
    cy: origin.y,
    alternateOuterRadius: 60,
    innerRadius: 20,
    outerRadius: 100,
    colors: star.colors,
    from: {
      innerRadius: 0,
      outerRadius: 0
    }
  }), /* Draw edges between major nodes first, so that nodes sit on top */
  showMajorEdges && nodes.map(function (node, index) {
    var majorNodeAngle = index * (360 / nodes.length);
    var majorNodePoint = cPoint(outerRing.r, clockAngle(majorNodeAngle));
    return React.createElement(My.MajorEdges, {
      mask: "url(#donut-hole)"
    }, /* draw a line between this and every other major node */
    nodes.map(function (siblingNode, siblingIndex) {
      // don't draw a line to itself
      if (index <= siblingIndex) return null;
      var otherAngle = siblingIndex * (360 / nodes.length);
      var cOtherPoint = cPoint(outerRing.r, clockAngle(otherAngle));
      return React.createElement(My.MajorEdge, {
        key: "edge-".concat(node.id, "-").concat(siblingNode.id),
        controlPoints: 1,
        t: 1,
        animate: isIndex,
        springConfig: {
          duration: 500
        },
        centerX: origin.x,
        centerY: origin.y,
        strokeWidth: 2,
        strokeDasharray: '5,5',
        strokeOpacity: 0.9,
        strokeColor: staticColors.edge.stroke,
        arrowSize: 0,
        curvature: 35,
        x1: majorNodePoint.cx,
        y1: majorNodePoint.cy,
        x2: cOtherPoint.cx,
        y2: cOtherPoint.cy,
        delay: linesDelay + lineDelay * index,
        from: {
          t: 0
        }
      });
    }));
  }), showMajorNodes && nodes.map(function (node, index) {
    /* Draw minor nodes for this major node */
    var majorNodeAngle = index * (360 / nodes.length);
    var majorNodePoint = cPoint(outerRing.r, clockAngle(majorNodeAngle));
    // console.log(`MINOR NODES ORIGIN ${node.name}`, innerRing.r * 2, majorNodeAngle - 180);
    // rather than drawing from the data viz circle origin, draw back inwards from the major node
    // this then allows us to draw multiple sets of nodes emerging from each major node
    // while also allowing the minor nodes to be correctly positioned for when we're just drawing the active set
    var minorNodeOriginAngle = majorNodeAngle - (minorMode === 'all' ? 90 : 270);
    var nodesOriginDelta = cPointReset(innerRing.r * 2, minorNodeOriginAngle);
    // console.log(`NODES ORIGIN DELTA`, nodesOriginDelta);
    var minorNodesOrigin = {
      cx: majorNodePoint.cx + nodesOriginDelta.cx,
      cy: majorNodePoint.cy + nodesOriginDelta.cy
    };
    var cPointMinorNode = cPointAtOrigin(minorNodesOrigin.cx, minorNodesOrigin.cy);
    // console.log(`NODES ORIGIN ${node.name}`, nodesOrigin);
    var drawMinorNodes = minorMode === 'all' || minorMode === 'active' && node.isActive;
    return drawMinorNodes && node.edges.map(function (edge, edgeIndex) {
      var minorNode = edge.node;
      var startAngle = (minorMode === 'all' ? majorNodeAngle - 180 : majorNodeAngle) + innerRing.childArc;
      var availableAngle = 360 - innerRing.childArc * 2;
      var angleStep = availableAngle / (node.edges.length - 1);
      var angle = clockAngle(startAngle + angleStep * edgeIndex);
      var point = cPointMinorNode(innerRing.r, angle);
      var delay = hasActiveMinorNode ? 0 : 200 + 20 * (edgeIndex + 1);
      var isActive = minorNode.isActive;
      var edgeColor = hasActiveMinorNode ? 'strong' : node.color.value;
      var edgeOpacity = hasActiveMinorNode ? isActive ? 1 : 0.1 : 1;
      var nodeProps = propMerge(innerRing.minorNode, {
        circle: {
          strokeWidth: isActive ? 3 : 2
        }
      });
      var animate = !hasActiveMinorNode && isOverview;
      return React.createElement("g", null, showMinorEdges && React.createElement(My.MinorEdge, {
        key: edge.id,
        controlPoints: 1,
        t: 1,
        delay: delay + 50,
        centerX: minorNodesOrigin.cx,
        centerY: minorNodesOrigin.cy,
        strokeWidth: edge.isActive ? 3 : 2,
        strokeDasharray: '5,5',
        strokeOpacity: edgeOpacity,
        strokeColor: edgeColor,
        arrowSize: 0,
        animate: animate,
        curvature: 100,
        intensity: 1,
        x1: majorNodePoint.cx,
        y1: majorNodePoint.cy,
        x2: point.cx,
        y2: point.cy,
        from: {
          t: 0
        }
      }), showMinorNodes && React.createElement(My.MinorNode, __assign({
        key: minorNode.id,
        entity: minorNode,
        x: point.cx,
        y: point.cy
      }, nodeProps, {
        animate: animate,
        from: {
          opacity: 0,
          x: majorNodePoint.cx,
          y: majorNodePoint.cy
        },
        angle: angle,
        delay: delay
      })));
    });
  }), /* Draw major nodes */
  showMajorNodes && nodes.map(function (node, index) {
    var angle = clockAngle(index * (360 / nodes.length));
    var point = cPoint(outerRing.r, angle);
    var isActive = node.isActive;
    var animate = !hasActiveMinorNode && isOverview;
    if (minorMode === 'all' || !hasActiveMajorNode || isActive) {
      var nodeProps = propMerge(outerRing.majorNode, {
        circle: {
          strokeWidth: isActive ? 3 : 2
        }
      });
      var fromOrigin = isActive ? {} : {
        x: origin.x,
        y: origin.y,
        opacity: 0
      };
      return React.createElement(My.MajorNode, __assign({
        key: node.id,
        entity: node,
        x: point.cx,
        y: point.cy,
        isActive: isActive
      }, nodeProps, {
        animate: animate,
        angle: minorMode === 'all' ? angle - 180 : 90,
        delay: nodeDelay * (index + 1),
        from: __assign(__assign({}, fromOrigin), {
          r: isActive ? outerRing.minorNode.r : 0
        })
      }));
    } else {
      var nodeProps = outerRing.minorNode;
      return React.createElement(My.MajorNode, __assign({
        key: node.id,
        entity: node,
        x: point.cx,
        y: point.cy,
        r: nodeProps.r,
        animate: animate,
        angle: angle
      }, nodeProps));
    }
  }));
} /*, (prevProps, nextProps) => {
  return prevProps.nodes.map(node => node.id).join('-') === nextProps.nodes.map(node => node.id).join('-');
  }*/);
/* @ts-ignore */
SvgEntityNavigator.defaultProps = SvgEntityNavigatorDefaultProps;
/* @ts-ignore */
SvgEntityNavigator.cls = cls;
var templateObject_1, templateObject_2, templateObject_3, templateObject_4;